export const calculatePrice = (
  price: number = 0,
  startDate: Date,
  endDate: Date,
  pickupTimeBlock?: string,
  dropoffTimeBlock?: string,
) => {
  const startDateTime = new Date(startDate!);
  const endDateTime = new Date(endDate!);

  // Use new calculation if time blocks are provided
  if (pickupTimeBlock && dropoffTimeBlock) {
    return calculatePriceWithTimeBlocks(
      price,
      startDateTime,
      endDateTime,
      pickupTimeBlock,
      dropoffTimeBlock,
    );
  }

  // Fallback to old calculation for backward compatibility
  const differenceInMs = startDateTime.getTime() - endDateTime.getTime();

  // Convert milliseconds to days
  const actualDaysDifference = Math.abs(
    Math.ceil(differenceInMs / (1000 * 60 * 60 * 24)),
  );

  const daysDifference = actualDaysDifference + 1;

  // Define the price per day, service charge, and tax
  const pricePerDay = price!; // $100 per day
  const serviceCharge = 0; // $50 service charge

  // Calculate the total cost
  const actualRentalPrice = daysDifference * pricePerDay;

  // calculate tax, 15 percent of the actual rentla price

  const tax = actualRentalPrice * 0.15; // $10 tax

  const totalPrice = actualRentalPrice + serviceCharge + tax;

  return {
    totalPrice,
    tax,
    serviceCharge,
    daysDifference,
    actualRentalPrice,
  };
};

/**
 * New calculation function that properly handles AM/PM time blocks
 * with the updated rounding rules
 */
export const calculatePriceWithTimeBlocks = (
  price: number = 0,
  startDate: Date,
  endDate: Date,
  pickupTimeBlock: string,
  dropoffTimeBlock: string,
) => {
  const pricePerDay = price;
  const serviceCharge = 0;

  // Calculate the number of days between start and end dates
  const startUTC = Date.UTC(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );
  const endUTC = Date.UTC(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
  );
  const diffInMS = Math.abs(endUTC - startUTC);
  const fullDays = Math.floor(diffInMS / (1000 * 60 * 60 * 24));

  // If it's the same day, fullDays will be 0
  // If it's consecutive days, fullDays will be 1, etc.

  // Calculate partial days based on AM/PM blocks
  let totalDays = 0;

  if (fullDays === 0) {
    // Same day rental
    if (
      pickupTimeBlock?.toUpperCase() === 'AM' &&
      dropoffTimeBlock?.toUpperCase() === 'AM'
    ) {
      totalDays = 0.5; // Half day
    } else if (
      pickupTimeBlock?.toUpperCase() === 'PM' &&
      dropoffTimeBlock?.toUpperCase() === 'PM'
    ) {
      totalDays = 0.5; // Half day
    } else {
      totalDays = 1; // Full day (AM to PM or PM to AM)
    }
  } else {
    // Multi-day rental
    // Start day: 0.5 if PM pickup, 1 if AM pickup
    const startDayValue = pickupTimeBlock?.toUpperCase() === 'PM' ? 0.5 : 1;

    // End day: 0.5 if AM dropoff, 1 if PM dropoff
    const endDayValue = dropoffTimeBlock?.toUpperCase() === 'AM' ? 0.5 : 1;

    // Middle days: fullDays represents the difference between dates
    // For Monday to Wednesday: fullDays = 2, but we want 1 middle day (Tuesday)
    // So middle days = fullDays - 1
    const middleDays = Math.max(0, fullDays - 1);

    totalDays = startDayValue + middleDays + endDayValue;
  }

  // Apply new rounding rules
  const billableDays = applyNewRoundingRules(totalDays);

  // Calculate the total cost
  const actualRentalPrice = billableDays * pricePerDay;
  const tax = actualRentalPrice * 0.15;
  const totalPrice = actualRentalPrice + serviceCharge + tax;

  return {
    totalPrice,
    tax,
    serviceCharge,
    daysDifference: billableDays,
    actualRentalPrice,
  };
};

/**
 * Applies the new rounding rules for rent calculation
 */
function applyNewRoundingRules(totalDays: number): number {
  // If total is exactly 0.5, round up to 1 day (minimum billing)
  if (totalDays === 0.5) {
    return 1;
  }

  // If total is a whole number + 0.5, round down to the nearest whole number
  if (totalDays % 1 === 0.5 && totalDays > 0.5) {
    return Math.floor(totalDays);
  }

  // If total is a whole number, bill it directly
  if (totalDays % 1 === 0) {
    return totalDays;
  }

  // For any other case, return as is (shouldn't happen with our logic)
  return totalDays;
}
